/**
 * Thin wrapper over GA4's global `gtag` for SPA route tracking.
 *
 * The GA snippet in index.html installs `window.gtag` asynchronously. This
 * module gracefully no-ops if analytics is blocked (uBlock, Brave shields,
 * Pi-hole, offline, etc.) — we never want a failing analytics call to
 * affect the user.
 */
type Gtag = (...args: unknown[]) => void;

function gtag(...args: unknown[]): void {
  const w = typeof window !== "undefined"
    ? (window as unknown as { gtag?: Gtag; dataLayer?: unknown[] })
    : null;
  if (!w) return;
  if (typeof w.gtag === "function") {
    w.gtag(...args);
    return;
  }
  // Fallback: push into dataLayer directly if the loader hasn't exposed
  // gtag yet. GA's bootstrap replays whatever's in dataLayer when it boots.
  if (Array.isArray(w.dataLayer)) w.dataLayer.push(args);
}

/** Fire a virtual page_view for a client-side route. */
export function trackPageView(path: string, title?: string): void {
  gtag("event", "page_view", {
    page_path: path,
    page_location:
      typeof window !== "undefined"
        ? `${window.location.origin}${path}`
        : path,
    page_title: title ?? (typeof document !== "undefined" ? document.title : undefined),
  });
}

/** Fire a one-off named event with arbitrary params. */
export function trackEvent(name: string, params: Record<string, unknown> = {}): void {
  gtag("event", name, params);
}
